import { useSyncExternalStore } from 'react';

// Super-admin tag rules (Tag Domain only), shared by every tag renderer.
let rules = [];
let classOrder = [];
const subs = new Set();
export function setTagRules(list) {
  rules = Array.isArray(list) ? list : [];
  subs.forEach((f) => f());
}
// Configured classification group order (names / codes, top first).
export function setTagClassOrder(list) {
  classOrder = Array.isArray(list) ? list : [];
  rules = [...rules];
  subs.forEach((f) => f());
}
export const getTagClassOrder = () => classOrder;
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };
export const getTagRules = () => rules;
export const useTagRules = () => useSyncExternalStore(subscribe, getTagRules);
// Signed-in user may see the merge preview and correct tag lines (super admin or granted tag admin).
let lineFixer = false;
export function setTagLineFixer(v) { lineFixer = !!v; subs.forEach((f) => f()); }
export const useTagLineFixer = () => useSyncExternalStore(subscribe, () => lineFixer);

export const RULE_FIELDS = [
  { id: 'material_type', label: 'Material type' },
  { id: 'code', label: 'Classification code' },
  { id: 'display_name', label: 'Tag display name' },
  { id: 'shape', label: 'Shape' },
  { id: 'size', label: 'Size' },
  { id: 'sieves', label: 'Sieve' },
  { id: 'note', label: 'Note' },
  { id: 'carats', label: 'Carats' },
  { id: 'pieces', label: 'Pieces' },
  { id: 'avg_size', label: 'Average size (ct ÷ pcs)' },
  { id: 'jewellery_type', label: 'Jewellery type' },
  { id: 'purity', label: 'Gold purity' },
];
export const RULE_OPS = [
  { id: 'eq', label: 'equals' }, { id: 'neq', label: 'not equals' }, { id: 'contains', label: 'contains' },
  { id: 'starts', label: 'starts with' }, { id: 'empty', label: 'is empty', noValue: true }, { id: 'not_empty', label: 'is not empty', noValue: true },
  { id: 'gte', label: '≥' }, { id: 'gt', label: '>' }, { id: 'lte', label: '≤' }, { id: 'lt', label: '<' },
  { id: 'between', label: 'between (≥ … and < …)', two: true },
];
export const RULE_PLACEHOLDERS = [
  { id: 'display_name', desc: 'Tag Display Name (else classification)' },
  { id: 'classification', desc: 'Original classification name' },
  { id: 'code', desc: 'Classification code' },
  { id: 'sieve', desc: 'Sieve size' },
  { id: 'shape', desc: 'Shape' },
  { id: 'size', desc: 'Size' },
  { id: 'note', desc: 'Issue note' },
  { id: 'carats', desc: 'Carats of the line' },
  { id: 'pieces', desc: 'Pieces of the line' },
  { id: 'avg_size', desc: 'Average size = ct ÷ pcs (2 decimals)' },
  { id: 'material', desc: 'Material type' },
];

// Largest number in a text; "6.1/2" / "6 1/2" = 6.5, "1/2" = 0.5.
export function sieveValue(text) {
  const t = String(text ?? '');
  const nums = [];
  const rest = t.replace(/(\d+)[.\s]+(\d+)\/(\d+)/g, (_, a, b, c) => { nums.push(+a + (+b / +c)); return ' '; })
    .replace(/(\d+)\/(\d+)/g, (_, b, c) => { nums.push(+b / +c); return ' '; });
  (rest.match(/\d+(?:\.\d+)?/g) || []).forEach((n) => nums.push(+n));
  return nums.length ? Math.max(...nums) : null;
}

function fieldValue(m, field, piece) {
  switch (field) {
    case 'material_type': return m.materialType || '';
    case 'code': return m.code || '';
    case 'display_name': return m.classification || '';
    case 'sieves': return m.sieves || '';
    case 'note': return m.note || '';
    case 'jewellery_type': return piece.jewelleryType || '';
    case 'purity': return piece.purity || '';
    case 'avg_size': return avgSize(m);
    default: return m[field] ?? '';
  }
}

function test(c, raw) {
  const s = String(raw ?? '').trim().toLowerCase();
  const v = String(c.value ?? '').trim().toLowerCase();
  const n = sieveValue(raw); const a = sieveValue(c.value); const b = sieveValue(c.value2);
  switch (c.op) {
    case 'eq': return s === v;
    case 'neq': return s !== v;
    case 'contains': return s.includes(v);
    case 'starts': return s.startsWith(v);
    case 'empty': return !s;
    case 'not_empty': return !!s;
    case 'gte': return n != null && a != null && n >= a;
    case 'gt': return n != null && a != null && n > a;
    case 'lte': return n != null && a != null && n <= a;
    case 'lt': return n != null && a != null && n < a;
    case 'between': return n != null && a != null && b != null && n >= a && n < b;
    default: return false;
  }
}

const avgSize = (m) => (Number(m.pieces) > 0 ? (Number(m.carats || 0) / Number(m.pieces)).toFixed(2) : '');

const fill = (tpl, m) => tpl.replace(/\{(\w+)\}/g, (all, k) => {
  const map = { display_name: m.classification, classification: m.rawName || m.classification, code: m.code, sieve: m.sieves, shape: m.shape, size: m.size, note: m.note, carats: m.carats, pieces: m.pieces, material: m.materialType, avg_size: avgSize(m) };
  return k in map ? String(map[k] ?? '') : all;
});

// Group lines by source classification (configured order first, else first appearance);
// inside a group, rule-labelled lines follow rule order, the rest keep their order.
function sortLines(lines, order) {
  const ord = (order || []).map((x) => String(x).trim().toUpperCase());
  const pos = (m) => {
    const i = ord.findIndex((o) => o === String(m.classification || '').toUpperCase() || o === String(m.code || '').toUpperCase());
    return i < 0 ? Infinity : i;
  };
  // Group by the original code (renamed lines stay with their source classification).
  const grp = (m) => String(m.code || m.rawName || m.classification || '').toUpperCase();
  const first = new Map();
  lines.forEach((m) => { if (!first.has(grp(m))) first.set(grp(m), m.idx); });
  return [...lines].sort((a, b) => (pos(a) - pos(b)) || (first.get(grp(a)) - first.get(grp(b))) || (a.rank - b.rank) || (a.idx - b.idx));
}

// Run every matching rule (top to bottom) on each line, then group merged lines.
export function applyTagRules(materials, view, list, piece = {}, order = [], split = false) {
  const active = (list || []).filter((r) => r.active !== false && (r.views === 'both' || r.views === view));
  const map = new Map();
  for (const [si, src] of (materials || []).entries()) {
    const m = { ...src, note: view === 'collapsed' ? '' : (src.note || (src.sieves ? `Sieve ${src.sieves}` : '')) };
    let mergeId = null;
    let rank = Infinity;
    const by = [];
    for (const [ri, r] of active.entries()) {
      if (!(r.conditions || []).every((c) => test(c, fieldValue(m, c.field, piece)))) continue;
      const a = r.actions || {};
      if (a.rename) m.classification = fill(a.rename, m);
      // Blank note box = no note on the tag; {note} keeps the portal note.
      m.note = a.note ? fill(a.note, m) : '';
      if (a.hide) m.hidden = true;
      if (a.hide_pieces) m.hidePieces = true;
      if (a.merge) mergeId = r.id;
      if (a.rename || a.merge) by.push(ruleLabel(r));
      if (a.note || a.rename || a.merge || a.hide_pieces) rank = ri;
      if (a.stop) break;
    }
    if (m.hidden) continue;
    // Identical display names (case/space-insensitive) become one line; raw keeps every line.
    const name = String(m.classification || '').trim().replace(/\s+/g, ' ').toLowerCase();
    const part = `${name}|${m.note}`;
    // Expanded: manual tags and colour stones keep each line unless this tag joins that name (Correct tag lines).
    const sep = view === 'expanded' && !split && !mergeId && (piece.manual || m.materialType === 'color_stone') && !(piece.join || []).includes(name);
    // Pricing (split) keeps one line per name + note so each keeps its own rate.
    const key = view === 'raw' && !mergeId ? m.key : split && !mergeId ? `p:${part}|${m.hidePieces ? 1 : 0}` : sep ? `u:${si}` : `n:${name}`;
    const info = { name: src.classification || '', note: m.note, carats: Number(m.carats || 0), pieces: Number(m.pieces || 0), dia: m.materialType === 'diamond', rules: by };
    const g = map.get(key);
    if (g) {
      g.partInfo.push(info);
      g.carats += Number(m.carats || 0); g.pieces += Number(m.pieces || 0); g.lines += 1; g.src.push(si);
      g.hidePieces = g.hidePieces && !!m.hidePieces;
      g.rank = Math.min(g.rank, rank);
      if (!g.note && m.note) g.note = m.note;
      if (!g.partKeys.includes(part)) g.partKeys.push(part);
      g.parts = g.partKeys.length;
    } else {
      map.set(key, { ...m, classification: key.startsWith('n:') ? String(m.classification || '').trim().replace(/\s+/g, ' ') : m.classification, key, partInfo: [info], partKeys: [part], parts: 1, hidePieces: !!m.hidePieces, carats: Number(m.carats || 0), pieces: Number(m.pieces || 0), lines: 1, rank, idx: map.size, src: [si] });
    }
  }
  // Non-diamond lines with 0 pieces (stones, pearls, other) show carats only.
  const out = Array.from(map.values()).map((g) => (!g.pieces && g.materialType !== 'diamond' ? { ...g, hidePieces: true } : g));
  // A saved line order (Reorder lines on a synced piece) is kept as is.
  return !piece.keepOrder && (active.length || (order || []).length) ? sortLines(out, order) : out;
}

const OP_SHORT = { eq: '=', neq: '≠', contains: 'contains', starts: 'starts with', empty: 'is empty', not_empty: 'is set', gte: '≥', gt: '>', lte: '≤', lt: '<', between: 'between' };
const FIELD_LABEL = Object.fromEntries(RULE_FIELDS.map((f) => [f.id, f.label]));

// Rule name, or an auto summary like "Shape = pear → rename".
export function ruleLabel(r) {
  if ((r.name || '').trim()) return r.name.trim();
  const c = (r.conditions || []).map((x) => `${FIELD_LABEL[x.field] || x.field} ${OP_SHORT[x.op] || x.op}${['empty', 'not_empty'].includes(x.op) ? '' : ` ${x.value}${x.op === 'between' ? `–${x.value2}` : ''}`}`).join(' & ');
  const a = r.actions || {};
  const t = [a.rename && 'rename', a.note ? `note "${a.note}"` : 'no note', a.merge && 'merge', a.hide && 'hide', a.hide_pieces && 'hide pcs', a.stop && 'stop'].filter(Boolean).join(', ');
  return `${c} → ${t}`;
}

// Active rules that match at least one stone line of a tag (in any view they apply to).
export function rulesAffecting(data, list) {
  const piece = { jewelleryType: data.jewelleryType, purity: data.purityText };
  const active = (list || []).filter((r) => r.active !== false);
  return active.filter((r) => (r.views === 'both' ? ['expanded', 'collapsed'] : [r.views]).some((view) => (
    (data.materials || []).some((src) => {
      const m = { ...src, note: view === 'collapsed' ? '' : (src.note || (src.sieves ? `Sieve ${src.sieves}` : '')) };
      for (const x of active) {
        if (!(x.views === 'both' || x.views === view)) continue;
        if (!(x.conditions || []).every((c) => test(c, fieldValue(m, c.field, piece)))) continue;
        if (x.id === r.id) return true;
        if (x.actions?.stop) return false;
      }
      return false;
    }))));
}

// Rules-window filter: 'match' (rule has a condition on the field that accepts the value),
// 'all' (no condition on that field, so it applies to every value), or null (hidden).
export function ruleFilterMatch(r, field, value) {
  if (!field) return 'match';
  const conds = (r.conditions || []).filter((c) => c.field === field);
  const v = String(value || '').trim();
  if (!conds.length) return v ? 'all' : null;
  if (!v) return 'match';
  const lv = v.toLowerCase();
  return conds.some((c) => test(c, v) || String(c.value || '').toLowerCase().includes(lv)) ? 'match' : null;
}
